import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SortIcon from '@mui/icons-material/Sort';
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Page from '../../components/Page';
import PendingTicketsMonitor from '../../components/PendingTicketsMonitor';
import TicketListModal from '../../components/TicketListModal';
import { AuthContext } from '../../context/Auth/AuthContext';
import socket from '../../hooks/useSocket';
import { UserCard } from '../../sections/@dashboard/user/cards';
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";

// Styled Components
const MonitorContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
    padding: 0,
    margin: 0,
}));

const MonitorHeader = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    padding: '12px 40px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
    flexWrap: 'wrap',
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1.25rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
}));

const HeaderControls = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
});

const LiveClock = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    height: 40,
    padding: '0 12px',
    backgroundColor: theme.palette.background.default,
    borderRadius: '6px',
    border: `1px solid ${theme.palette.divider}`,
}));

const ClockText = styled(Typography)(({ theme }) => ({
    fontSize: '0.85rem',
    fontWeight: 500,
    fontFamily: 'monospace',
    color: theme.palette.text.primary,
}));

const MainContentContainer = styled(Box)(() => ({
    display: 'grid',
    gridTemplateColumns: '420px 1fr',
    gap: '24px',
    padding: '24px 40px 40px 40px',
    height: 'calc(100vh - 100px)',
    minHeight: '600px',
    '@media (max-width: 1400px)': {
        gridTemplateColumns: '380px 1fr',
    },
    '@media (max-width: 1200px)': {
        gridTemplateColumns: '1fr',
        height: 'auto',
    },
}));

const PendingListContainer = styled(Box)(() => ({
    height: '100%',
    minHeight: '600px',
    '@media (max-width: 1200px)': {
        height: '500px',
    },
}));

const UsersSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: theme.palette.background.paper,
    borderRadius: '12px',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
}));

const UsersListHeader = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    gap: '12px',
    flexWrap: 'wrap',
    flexShrink: 0,
}));

const UsersListTitle = styled(Typography)(({ theme }) => ({
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
}));

const ControlsContainer = styled(Box)({
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    flexWrap: 'wrap',
});

const UsersGridContainer = styled(Box)(({ theme }) => ({
    flex: 1,
    overflow: 'auto',
    padding: '12px',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.mode === 'dark' ? '#3a3a3a' : '#d0d0d0',
        borderRadius: '3px',
    },
}));

const UsersGrid = styled(Box)(() => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '12px',
    '@media (max-width: 1600px)': {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },
    '@media (max-width: 1200px)': {
        gridTemplateColumns: 'repeat(3, 1fr)',
    },
    '@media (max-width: 900px)': {
        gridTemplateColumns: 'repeat(2, 1fr)',
    },
    '@media (max-width: 600px)': {
        gridTemplateColumns: 'repeat(1, 1fr)',
    },
}));

const UpdateIndicator = styled(Box)(({ theme }) => ({
    position: 'fixed',
    bottom: '16px',
    right: '16px',
    padding: '6px 12px',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.secondary,
    borderRadius: '6px',
    border: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.75rem',
    zIndex: 1000,
}));

// Utility functions
const generateUuid = () => {
    return crypto.randomUUID ? crypto.randomUUID() : (
        [1e7] + -1e3 + -4e3 + -8e3 + -1e11
    ).replace(/[018]/g, (c) =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> (c / 4)).toString(16)
    );
};

const padTo2Digits = (num) => String(num).padStart(2, '0');

const isTimeInRange = (currentTime, startTime, endTime) => {
    const dataBase = '2023-04-04T';
    const time = new Date(`${dataBase}${currentTime}:00`);
    const timeStart = new Date(`${dataBase}${startTime}:00`);
    const timeEnd = new Date(`${dataBase}${endTime}:00`);
    return time >= timeStart && time <= timeEnd;
};

const ITEM_HEIGHT = 40;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 6 + ITEM_PADDING_TOP,
        },
    },
};

// Utilitários de formato — agora em escopo de módulo, fora do Monitor.
const formatTime = (date) => date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
});

const formatDate = (date) => date.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'short'
});

// Sub-componentes auto-contidos: têm seu próprio state/interval.
// Antes o Monitor inteiro re-renderizava 1x/segundo (com o relógio em
// useState do Monitor), arrastando UserCard/PendingTicketsMonitor junto.
// Agora só estes pequenos isolam o tick.
const HeaderClock = React.memo(() => {
    const [now, setNow] = React.useState(() => new Date());
    React.useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    return <ClockText>{formatTime(now)}</ClockText>;
});

const HeaderDate = React.memo(() => {
    // A data muda 1x ao dia; basta verificar a cada minuto.
    const [now, setNow] = React.useState(() => new Date());
    React.useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 60000);
        return () => clearInterval(id);
    }, []);
    return (
        <Typography variant="caption" color="text.secondary">
            {formatDate(now)}
        </Typography>
    );
});

const Monitor = () => {
    const { user } = useContext(AuthContext);
    const [hash] = useState(generateUuid());
    const [usersWorking, setUsersWorking] = useState([]);
    const [lastUpdate, setLastUpdate] = useState(new Date());
    const [sortOrder, setSortOrder] = useState('desc');
    const [rankingMode, setRankingMode] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);

    // Filtros
    const [queues, setQueues] = useState([]);
    const [selectedQueueIds, setSelectedQueueIds] = useState([]);
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const [showDayOff, setShowDayOff] = useState(false);
    const [showOffShift, setShowOffShift] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Buscar filas
    useEffect(() => {
        const fetchQueues = async () => {
            try {
                const { data } = await api.get('/queue');
                setQueues(data);
            } catch (error) {
                console.error('Error fetching queues:', error);
            }
        };
        fetchQueues();
    }, []);

    // O relógio agora vive em <HeaderClock /> e <HeaderDate /> (componentes
    // isolados no topo do arquivo). Antes este interval atualizava state do
    // Monitor a cada 1s, fazendo tudo (incluindo a grade de UserCards) renderizar.

    // Get working periods
    const getPeriods = useCallback(async () => {
        const now = new Date();
        const hours = now.getHours();
        const minutes = now.getMinutes();
        const currentTime = `${padTo2Digits(hours)}:${padTo2Digits(minutes)}`;

        try {
            const { data } = await api.get('/settings-company');
            const findPeriodOfWork = data?.findPeriodOfWork;

            if (findPeriodOfWork) {
                return ['morning', 'afternoon', 'night'].filter((period) => {
                    const { start, end } = JSON.parse(findPeriodOfWork[period]);
                    return isTimeInRange(currentTime, start, end);
                });
            }
            return ['morning', 'afternoon', 'night'];
        } catch (error) {
            console.error('Error getting company settings:', error);
            return ['morning', 'afternoon', 'night'];
        }
    }, []);

    // Reload monitor data
    const reloadListing = useCallback(async () => {
        setIsRefreshing(true);
        const periods = await getPeriods();
        socket.emit('reloadMonitor', {
            period: periods,
            companyId: user.companyId,
            hash: hash,
        });
    }, [getPeriods, user.companyId, hash]);

    const getMonitorUsers = useCallback(async () => {
        try {
            const { data } = await api.get('/tickets-monitor', {
                params: {
                    dateStart: new Date().toISOString(),
                    dateEnd: new Date().toISOString(),
                },
            });
            setUsersWorking(data.users);
            setLastUpdate(new Date());
        } catch (err) {
            console.error('Error fetching monitor users:', err);
            ToastError(err);
        }
    }, []);

    // Handle socket update
    const handleUpdateMonitor = useCallback((data) => {
        setUsersWorking(data);
        setLastUpdate(new Date());
        setIsRefreshing(false);
    }, []);

    // Setup socket and intervals
    useEffect(() => {
        const socketEventName = `updateMonitor_${hash}`;
        socket.on(socketEventName, handleUpdateMonitor);

        // Carga inicial: busca via API, depois ativa socket
        const init = async () => {
            await getMonitorUsers();
            await reloadListing();
        };
        init();

        // Atualizações subsequentes: apenas via socket (sem refetch API)
        const intervalId = setInterval(reloadListing, 30000);

        return () => {
            clearInterval(intervalId);
            socket.off(socketEventName, handleUpdateMonitor);
        };
    }, [hash, getMonitorUsers, reloadListing, handleUpdateMonitor]);

    // formatTime/formatDate moveram para escopo de módulo (topo do arquivo).

    // Filtrar e ordenar atendentes
    const filteredAndSortedUsers = useMemo(() => {
        let filtered = [...usersWorking];

        // Filtrar por usuários selecionados
        if (selectedUserIds.length > 0) {
            filtered = filtered.filter(u => selectedUserIds.includes(u.id));
        }

        // Filtrar por folga: ocultar usuários em folga se toggle desativado
        if (!showDayOff) {
            filtered = filtered.filter(u => !u.isOnDayOff);
        }

        // Filtrar por plantão: ocultar usuários fora do plantão se toggle desativado
        if (!showOffShift) {
            filtered = filtered.filter(u => u.isOnShift !== false);
        }

        // Ordenar
        filtered.sort((a, b) => {
            if (sortOrder === 'desc') {
                return b.totalTickets - a.totalTickets;
            } else {
                return a.totalTickets - b.totalTickets;
            }
        });

        return filtered;
    }, [usersWorking, selectedUserIds, showDayOff, showOffShift, sortOrder]);

    // Handlers
    const handleSortChange = (_, newSort) => {
        if (newSort !== null) {
            setSortOrder(newSort);
        }
    };

    const handleRankingToggle = () => {
        setRankingMode(!rankingMode);
    };

    const handleOpenModal = (user) => {
        setSelectedUser(user);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedUser(null);
    };

    const handleQueueChange = (event) => {
        const value = event.target.value;
        // Verifica se clicou em "Todos"
        if (value.includes('all')) {
            if (selectedQueueIds.length === queues.length) {
                setSelectedQueueIds([]);
            } else {
                setSelectedQueueIds(queues.map(q => q.id));
            }
            return;
        }
        setSelectedQueueIds(typeof value === 'string' ? value.split(',') : value);
    };

    const handleUserChange = (event) => {
        const value = event.target.value;
        // Verifica se clicou em "Todos"
        if (value.includes('all')) {
            if (selectedUserIds.length === usersWorking.length) {
                setSelectedUserIds([]);
            } else {
                setSelectedUserIds(usersWorking.map(u => u.id));
            }
            return;
        }
        setSelectedUserIds(typeof value === 'string' ? value.split(',') : value);
    };

    return (
        <Page title="Monitor de Atendimentos">
            <MonitorContainer>
                {/* Header */}
                <MonitorHeader>
                    <Box>
                        <HeaderTitle>Monitor de Atendimentos</HeaderTitle>
                        <HeaderDate />
                    </Box>

                    <HeaderControls>
                        {/* Filtro de Filas */}
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Filas</InputLabel>
                            <Select
                                multiple
                                value={selectedQueueIds}
                                onChange={handleQueueChange}
                                input={<OutlinedInput label="Filas" />}
                                renderValue={(selected) =>
                                    selected.length === 0
                                        ? 'Todas'
                                        : `${selected.length} fila${selected.length > 1 ? 's' : ''}`
                                }
                                MenuProps={MenuProps}
                                sx={{ fontSize: '0.85rem' }}
                            >
                                <MenuItem value="all">
                                    <Checkbox
                                        checked={selectedQueueIds.length === queues.length && queues.length > 0}
                                        indeterminate={selectedQueueIds.length > 0 && selectedQueueIds.length < queues.length}
                                        size="small"
                                    />
                                    <ListItemText primary="Todos" sx={{ '& .MuiTypography-root': { fontWeight: 600, fontSize: '0.85rem' } }} />
                                </MenuItem>
                                <Divider sx={{ my: 0.5 }} />
                                {queues.map((queue) => (
                                    <MenuItem key={queue.id} value={queue.id}>
                                        <Checkbox
                                            checked={selectedQueueIds.includes(queue.id)}
                                            size="small"
                                        />
                                        <Box
                                            sx={{
                                                width: 8,
                                                height: 8,
                                                borderRadius: '50%',
                                                backgroundColor: queue.color,
                                                mr: 1,
                                            }}
                                        />
                                        <ListItemText primary={queue.name} sx={{ '& .MuiTypography-root': { fontSize: '0.85rem' } }} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Filtro de Atendentes */}
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                            <InputLabel>Atendentes</InputLabel>
                            <Select
                                multiple
                                value={selectedUserIds}
                                onChange={handleUserChange}
                                input={<OutlinedInput label="Atendentes" />}
                                renderValue={(selected) =>
                                    selected.length === 0
                                        ? 'Todos'
                                        : `${selected.length} selecionado${selected.length > 1 ? 's' : ''}`
                                }
                                MenuProps={MenuProps}
                                sx={{ fontSize: '0.85rem' }}
                            >
                                <MenuItem value="all">
                                    <Checkbox
                                        checked={selectedUserIds.length === usersWorking.length && usersWorking.length > 0}
                                        indeterminate={selectedUserIds.length > 0 && selectedUserIds.length < usersWorking.length}
                                        size="small"
                                    />
                                    <ListItemText primary="Todos" sx={{ '& .MuiTypography-root': { fontWeight: 600, fontSize: '0.85rem' } }} />
                                </MenuItem>
                                <Divider sx={{ my: 0.5 }} />
                                {usersWorking.map((userItem) => (
                                    <MenuItem key={userItem.id} value={userItem.id}>
                                        <Checkbox
                                            checked={selectedUserIds.includes(userItem.id)}
                                            size="small"
                                        />
                                        <ListItemText primary={userItem.name} sx={{ '& .MuiTypography-root': { fontSize: '0.85rem' } }} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        {/* Filtros de folga e plantão */}
                        <Button
                            variant={showDayOff ? "contained" : "outlined"}
                            color={showDayOff ? "error" : "inherit"}
                            onClick={() => setShowDayOff(!showDayOff)}
                            startIcon={<EventBusyIcon sx={{ fontSize: '18px !important' }} />}
                            sx={{
                                height: 40,
                                textTransform: 'none',
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                borderColor: showDayOff ? undefined : 'divider',
                                color: showDayOff ? undefined : 'text.secondary',
                                px: 2,
                            }}
                        >
                            Em folga
                        </Button>
                        <Button
                            variant={showOffShift ? "contained" : "outlined"}
                            color={showOffShift ? "warning" : "inherit"}
                            onClick={() => setShowOffShift(!showOffShift)}
                            startIcon={<ScheduleIcon sx={{ fontSize: '18px !important' }} />}
                            sx={{
                                height: 40,
                                textTransform: 'none',
                                fontSize: '0.85rem',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                                borderColor: showOffShift ? undefined : 'divider',
                                color: showOffShift ? undefined : 'text.secondary',
                                px: 2,
                            }}
                        >
                            Fora do plantão
                        </Button>

                        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                        <LiveClock>
                            <AccessTimeIcon sx={{ fontSize: 18 }} />
                            <HeaderClock />
                        </LiveClock>
                    </HeaderControls>
                </MonitorHeader>

                {/* Container Principal */}
                <MainContentContainer>
                    {/* Coluna Esquerda - Tickets Pendentes */}
                    <PendingListContainer>
                        <PendingTicketsMonitor selectedQueueIds={selectedQueueIds} />
                    </PendingListContainer>

                    {/* Coluna Direita - Lista de Atendentes */}
                    <UsersSection>
                        <UsersListHeader>
                            <UsersListTitle>
                                Atendentes ({filteredAndSortedUsers.length})
                            </UsersListTitle>
                            <ControlsContainer>
                                <ToggleButtonGroup
                                    value={sortOrder}
                                    exclusive
                                    onChange={handleSortChange}
                                    size="small"
                                >
                                    <ToggleButton value="desc" sx={{ px: 1.5, py: 0.4, fontSize: '0.75rem', textTransform: 'none' }}>
                                        <SortIcon sx={{ mr: 0.5, fontSize: 16 }} />
                                        Mais
                                    </ToggleButton>
                                    <ToggleButton value="asc" sx={{ px: 1.5, py: 0.4, fontSize: '0.75rem', textTransform: 'none' }}>
                                        <SortIcon sx={{ mr: 0.5, fontSize: 16, transform: 'rotate(180deg)' }} />
                                        Menos
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                <Button
                                    variant={rankingMode ? "contained" : "outlined"}
                                    onClick={handleRankingToggle}
                                    startIcon={<EmojiEventsIcon sx={{ fontSize: '16px !important' }} />}
                                    size="small"
                                    sx={{ py: 0.4, textTransform: 'none', fontSize: '0.75rem' }}
                                >
                                    Ranking
                                </Button>
                            </ControlsContainer>
                        </UsersListHeader>

                        {/* Grid de Atendentes */}
                        <UsersGridContainer>
                            <UsersGrid>
                                {filteredAndSortedUsers.map((userItem, index) => (
                                    <UserCard
                                        key={userItem.id}
                                        user={userItem}
                                        rankingMode={rankingMode}
                                        position={index + 1}
                                        onTicketClick={handleOpenModal}
                                        isOnDayOff={userItem.isOnDayOff}
                                        isOnShift={userItem.isOnShift}
                                    />
                                ))}
                            </UsersGrid>
                        </UsersGridContainer>
                    </UsersSection>
                </MainContentContainer>

                {/* Indicador de atualização */}
                <UpdateIndicator>
                    {isRefreshing ? (
                        <>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: 'warning.main',
                                    animation: 'pulse 1s infinite',
                                    '@keyframes pulse': {
                                        '0%, 100%': { opacity: 1 },
                                        '50%': { opacity: 0.3 },
                                    },
                                }}
                            />
                            Atualizando...
                        </>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: 'success.main',
                                }}
                            />
                            Última atualização às {formatTime(lastUpdate)}
                        </>
                    )}
                </UpdateIndicator>

                <TicketListModal
                    open={modalOpen}
                    onClose={handleCloseModal}
                    user={selectedUser}
                />
            </MonitorContainer>
        </Page>
    );
};

export default Monitor;
